/**
 * @file        src/components/PhoneInput.jsx
 * @description Phone number input with a country-code selector. Defaults to
 *              India (+91). Fully controlled: it parses the incoming `value`
 *              (e.g. "+91 98765 43210") into { code, number } and emits the
 *              combined string back via onChange, so existing callers that store
 *              a single phone string keep working. Renders bare (transparent) so
 *              it drops into the app's existing field wrappers. The code picker
 *              is a custom neon-dark dropdown (not a native <select>) showing the
 *              5 most common countries with a Show more / less toggle.
 * @author      Kinetika Labs
 * @created     2026-06-26
 * @changelog
 *   - 2026-07-16 Claude — Theme fix: replaced hardcoded colors with theme tokens so the screen recolors on Light/Dark switch.
 *   - 2026-06-26 Claude — Initial. Shared country-code phone input (default IN).
 *   - 2026-06-26 Claude — Replaced native select with a styled dropdown; show 5
 *                          countries by default + Show more / less.
 */
import React, { useState, useRef, useEffect } from 'react';

// Common dialling codes (India first / default). [dialCode, ISO2, name].
// The first 5 show by default; the rest are behind "Show more".
export const COUNTRY_CODES = [
  ['+91', 'IN', 'India'],
  ['+1', 'US', 'United States'],
  ['+44', 'GB', 'United Kingdom'],
  ['+971', 'AE', 'UAE'],
  ['+65', 'SG', 'Singapore'],
  ['+61', 'AU', 'Australia'],
  ['+60', 'MY', 'Malaysia'],
  ['+66', 'TH', 'Thailand'],
  ['+966', 'SA', 'Saudi Arabia'],
  ['+974', 'QA', 'Qatar'],
  ['+49', 'DE', 'Germany'],
  ['+33', 'FR', 'France'],
  ['+39', 'IT', 'Italy'],
  ['+81', 'JP', 'Japan'],
  ['+86', 'CN', 'China'],
  ['+94', 'LK', 'Sri Lanka'],
  ['+880', 'BD', 'Bangladesh'],
  ['+977', 'NP', 'Nepal'],
];

const DEFAULT_VISIBLE = 5;

// Accepted national-number digit counts per dial code. Arrays allow countries
// with more than one valid length. Unlisted codes fall back to a 7–15 range.
const PHONE_LENGTHS = {
  '+91': [10],   // India
  '+1': [10],    // US / Canada
  '+44': [10],   // UK
  '+971': [9],   // UAE
  '+65': [8],    // Singapore
  '+61': [9],    // Australia
  '+60': [9, 10], // Malaysia
  '+66': [9],    // Thailand
  '+966': [9],   // Saudi Arabia
  '+974': [8],   // Qatar
  '+49': [10, 11], // Germany
  '+33': [9],    // France
  '+39': [9, 10], // Italy
  '+81': [10],   // Japan
  '+86': [11],   // China
  '+94': [9],    // Sri Lanka
  '+880': [10],  // Bangladesh
  '+977': [10],  // Nepal
};

// Known codes longest-first so "+91" is matched before "+9", "+971" before "+97".
const KNOWN_CODES = [...new Set(COUNTRY_CODES.map((c) => c[0]))].sort((a, b) => b.length - a.length);

const countryName = (code) => (COUNTRY_CODES.find((c) => c[0] === code) || [])[2] || 'this country';

// '' when the number is valid (or empty — emptiness is the caller's concern),
// else a human message naming the expected length for the chosen country.
export function phoneError(value) {
  const { code, number } = splitPhone(value);
  const digits = number.replace(/\D/g, '');
  if (!digits) return '';
  const lens = PHONE_LENGTHS[code];
  if (lens) {
    if (!lens.includes(digits.length)) {
      return `${countryName(code)} mobile numbers must be ${lens.join(' or ')} digits`;
    }
    return '';
  }
  if (digits.length < 7 || digits.length > 15) return 'Enter a valid mobile number';
  return '';
}

// True only when the number is present AND matches the country's length rule.
export function isValidPhone(value) {
  const { number } = splitPhone(value);
  return number.replace(/\D/g, '').length > 0 && !phoneError(value);
}

// ISO2 → flag emoji (regional indicator letters).
function flag(iso) {
  return String(iso || '')
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));
}

// Split a stored phone string into { code, number }. Defaults to +91.
export function splitPhone(value) {
  const s = String(value || '').trim();
  if (s.startsWith('+')) {
    const found = KNOWN_CODES.find((c) => s.startsWith(c));
    if (found) return { code: found, number: s.slice(found.length).trim() };
    return { code: '+91', number: s.replace(/^\+/, '').trim() };
  }
  return { code: '+91', number: s };
}

export default function PhoneInput({
  value,
  onChange,
  onBlur,
  placeholder,
  inputClassName = '',
}) {
  const { code, number } = splitPhone(value);
  const current = COUNTRY_CODES.find((c) => c[0] === code) || COUNTRY_CODES[0];
  const lens = PHONE_LENGTHS[code];
  const maxLen = lens ? Math.max(...lens) : 15;
  // Allow only digits/spaces, capped at the country's longest valid length.
  const emit = (c, n) => {
    const cleaned = String(n).replace(/[^\d\s]/g, '');
    const digits = cleaned.replace(/\D/g, '').slice(0, PHONE_LENGTHS[c] ? Math.max(...PHONE_LENGTHS[c]) : 15);
    onChange?.(`${c} ${digits}`.trim());
  };
  const ph = placeholder || (lens ? `${maxLen}-digit number` : 'Mobile number');

  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ddRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ddRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (c) => { emit(c, number); setOpen(false); setShowAll(false); };
  const list = showAll ? COUNTRY_CODES : COUNTRY_CODES.slice(0, DEFAULT_VISIBLE);

  return (
    <div className="flex items-center gap-2">
      <div className="relative shrink-0" ref={ddRef}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 text-[15px] font-medium cursor-pointer hover:text-neon transition"
          aria-label="Country code"
        >
          <span className="text-base leading-none">{flag(current[1])}</span>
          <span>{current[0]}</span>
          <svg viewBox="0 0 24 24" className={'w-3.5 h-3.5 text-muted transition-transform ' + (open ? 'rotate-180' : '')}><path fill="currentColor" d="m7 10 5 5 5-5z" /></svg>
        </button>

        {open && (
          <div className="absolute z-[120] top-full left-0 mt-2 w-60 rounded-xl border border-line bg-panel shadow-2xl overflow-hidden">
            <ul className="py-1">
              {list.map(([c, iso, name]) => {
                const on = c === code;
                return (
                  <li key={`${c}-${iso}`}>
                    <button
                      type="button"
                      onClick={() => pick(c)}
                      className={'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm cursor-pointer transition ' + (on ? 'bg-neon/10 text-neon' : 'hover:bg-ink/5')}
                    >
                      <span className="text-base leading-none">{flag(iso)}</span>
                      <span className="flex-1 truncate">{name}</span>
                      <span className="text-muted text-xs">{c}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {COUNTRY_CODES.length > DEFAULT_VISIBLE && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="w-full px-3 py-2 text-[12px] font-semibold text-neon border-t border-line hover:bg-neon/5 cursor-pointer transition"
              >{showAll ? 'Show less' : `Show more (${COUNTRY_CODES.length - DEFAULT_VISIBLE})`}</button>
            )}
          </div>
        )}
      </div>

      <span className="text-line select-none">|</span>

      <input
        type="tel"
        inputMode="numeric"
        maxLength={maxLen}
        value={number}
        onChange={(e) => emit(code, e.target.value)}
        onBlur={onBlur}
        placeholder={ph}
        className={'w-full bg-transparent outline-none text-[15px] placeholder:text-muted/60 ' + inputClassName}
      />
    </div>
  );
}
